import { memo, useMemo } from 'react'
import type { FC } from 'react'
import type { NextPage, GetStaticPropsContext, GetStaticPropsResult } from 'next'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { callAPI } from '@/api'
import { HeadTag, Layout, HeaderContents, SectionContents } from '@/components'
import { Page } from '@/schema'
import { createGrid, getMargin60 } from '@/styles'
import { callLocales } from '@/i18n'

type TypeContentsPage = {
  data: Page,
  user?: any
}

const PRO = process.env.SITE_ENVIRONMENT !== 'development' && process.env.SITE_ENVIRONMENT !== 'local' && process.env.SITE_ENVIRONMENT !== 'staging' && process.env.SITE_ENVIRONMENT !== 'preview'

// REVIEW the pages: 'manifiesto' and 'receta-de-octubre' for production
const isTestPage = (slug) => {
  if(
    !slug ||
    slug.indexOf('test') !== -1 ||
    slug.indexOf('sample') !== -1 ||
    slug.indexOf('prueba') !== -1 ||
    slug.indexOf('abc') !== -1 ||
    slug.indexOf('aaaaaaa') !== -1 ||
    slug.indexOf('non-si-gioca-con-il-cibo') !== -1 ||
    (!PRO && slug.indexOf('receta-octubre') !== -1) ||
    (!PRO && slug.indexOf('sobre-nosotros') !== -1) ||
    (!PRO && slug.indexOf('aviso-legal') !== -1) ||
    // (!PRO && slug.indexOf('manifiesto') !== -1) ||
    // (!PRO && slug.indexOf('manifesto') !== -1) ||
    slug.indexOf('dy-') !== -1) {
    return true
  }

  return false
}

export async function getStaticPaths({ locales }) {
  const [data, error] = await callAPI({ type: 'pages' })
  if (error) return { paths: [], fallback: 'blocking' }

  const paths = []
  locales.map((locale) => data.map((page) => {
    const slug = page.slug[locale]

    if(!isTestPage(slug)) {
      const params = { slug }
      const obj = { params, locale }
      paths.push(obj)
    }
  }))

  return { paths, fallback: 'blocking' }
}

type Props = {
  data: any,
  locales: any,
  layoutLocale: string,
  layout: any
}

export async function getStaticProps({ params, locale }:GetStaticPropsContext<{ slug: string }>):Promise<GetStaticPropsResult<Props>> {
  const { slug } = params
  const [data, error] = await callAPI({ type: 'page', params: { slug, locale } })
  const [locales, layoutLocale] = await callLocales({ type: 'home', locale })
  const [layoutData, layoutError] = await callAPI({ type: 'layout',  params: { locale } })

  if (error || !data || layoutError) return { notFound: true }

  return {
    props: {
      data,
      locales,
      layoutLocale, 
      layout: layoutData
    },
    revalidate: 300, // In seconds
  }
}

const Main = styled.main`
  ${createGrid()}
  ${getMargin60()}
`

// ORIGINAL
// const getContentsSections = (sections) => sections.map((section, idx) => {
//   return (
//     <SectionContents key={idx} section={section} />
//   )
// })
// CMS HACK
// added hack to simulate content with 2 rows ( not yet ready in the CMS)
const getContentsSections = (sections = []) => {
  let jumpIdx = null

  return sections.map((section, idx) => {
    if (jumpIdx && jumpIdx === idx) {
      jumpIdx = null
    } else {
      let columnsGrouped = null

      if(section.rows === 2) {
        jumpIdx = idx + 1
        const nextSectionColumns = sections[idx + 1]?.columns
        columnsGrouped = [...section.columns, ...nextSectionColumns]
      }

      return (
        <SectionContents key={idx} section={columnsGrouped ? { rows: section.rows, columns: columnsGrouped } : section} />
      )
    }
  })
}

const ContentsPage: NextPage<TypeContentsPage> & { Layout?:FC } = memo(({ data }) => {
  const { isFallback } = useRouter() || { isFallback: true }

  const { seo, slug, title, subtitle, contents } = data || {}
  const ContentsSections = useMemo(() => getContentsSections(contents), [contents])

  if (isFallback || !seo) return <></>
  return (
    <>
      <HeadTag data={seo}  jsonld={{ data, pageType: 'contents' }} pathname='page/' slug={slug} />
      <HeaderContents>
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </HeaderContents>
      <Main>
        {ContentsSections}
      </Main>
    </>
  )
})

ContentsPage.Layout = Layout
export default ContentsPage
